import { isAfter, isBefore, isValid } from 'date-fns';
import { ChangeEventHandler, FC, useEffect, useState } from 'react';
import { OnChangeDateRangeCallback } from 'react-calendar';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Button, Calendar, Form } from '@base-app';
import { COMMON_TRANSLATION } from '@common/constants';
import { Variants } from '@common/types';
import { clearTimeFromDate, toLocaleDateString } from '@common/utils';

import InputDate from '../InputDate/InputDate';
import { SelectDateRangeModalFormValues } from '../SelectDateRangeModal.types';
import { DateRangeFormProps } from './DateRangeForm.types';
import { INPUT_DATE_PATTERN } from '@/common/components/constants';

const DateRangeForm: FC<DateRangeFormProps> = ({ startDate, endDate, maxDate, minDate, onSubmit, setRangeError }) => {
  const [maxFromDate, setMaxFromDate] = useState<Date | undefined>();
  const [minToDate, setMinToDate] = useState<Date | undefined>();
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const { t } = useTranslation(COMMON_TRANSLATION);

  const defaultValues = {
    fromDate: '',
    toDate: '',
  };

  const methods = useForm<SelectDateRangeModalFormValues>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, trigger, setValue } = methods;

  const handleFromChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = (e.currentTarget as any)?.value;
    const date = value ? new Date(value) : null;

    if (INPUT_DATE_PATTERN.test(value) && isValid(date)) {
      setFromDate(date);
    }
  };

  const handleToChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = (e.currentTarget as any)?.value;
    const date = value ? new Date(value) : null;

    if (INPUT_DATE_PATTERN.test(value) && isValid(date)) {
      setToDate(date);
    }
  };

  const handleChange: OnChangeDateRangeCallback = (dates, _event) => {
    const [from, to] = dates;
    setFromDate(from);
    setToDate(to ?? null);
  };

  const checkMaxFromDate = (toDate: Date | null, maxDate: Date | undefined): Date | undefined => {
    if (maxDate && toDate) {
      return isBefore(clearTimeFromDate(maxDate), clearTimeFromDate(toDate)) ? maxDate : toDate;
    }

    if (toDate) return toDate;
    if (maxDate) return maxDate;
  };
  const checkMinToDate = (fromDate: Date | null, minDate: Date | undefined): Date | undefined => {
    if (minDate && fromDate) {
      return isAfter(clearTimeFromDate(minDate), clearTimeFromDate(fromDate)) ? minDate : fromDate;
    }

    if (fromDate) return fromDate;
    if (minDate) return minDate;
  };

  // set current week by default.
  useEffect(() => {
    const today = new Date();
    if (!startDate) {
      const firstkDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
      setFromDate(firstkDayOfWeek);
    }
    if (!endDate) setToDate(today);
  }, []);

  useEffect(() => {
    startDate && setFromDate(startDate);
    endDate && setToDate(endDate);
  }, [startDate, endDate]);

  useEffect(() => {
    fromDate && setValue('fromDate', toLocaleDateString(fromDate));
    toDate && setValue('toDate', toLocaleDateString(toDate));

    setMaxFromDate(checkMaxFromDate(toDate, maxDate));
    setMinToDate(checkMinToDate(fromDate, minDate));

    if (minDate === undefined || maxDate === undefined || fromDate === null || toDate === null) {
      return;
    }

    if (isBefore(clearTimeFromDate(fromDate), minDate) || isAfter(clearTimeFromDate(toDate), maxDate)) {
      setRangeError(true);
    } else {
      setRangeError(false);
    }
  }, [fromDate, toDate]);

  // re-validate fields after any of calculated min and max dates values change.
  useEffect(() => {
    const triggerFields = async (): Promise<void> => {
      try {
        await trigger();
      } catch (err) {}
    };

    void triggerFields();
  }, [minToDate, maxFromDate]);

  return (
    <FormProvider {...methods}>
      <Form
        data-testid="select-date-range-form"
        css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box css={{ display: 'flex', justifyContent: 'center' }}>
          <InputDate
            css={{ marginRight: '$2' }}
            deps={['toDate']}
            id="select-date-range-from"
            label={t('from')}
            maxDate={maxFromDate}
            minDate={minDate}
            name="fromDate"
            onChange={handleFromChange}
            required
            value={fromDate}
            variant={Variants.SECONDARY}
            width={'50%'}
          />
          <InputDate
            deps={['fromDate']}
            id="select-date-range-to"
            label={t('to')}
            maxDate={maxDate}
            minDate={minToDate}
            name="toDate"
            onChange={handleToChange}
            required
            value={toDate}
            variant={Variants.SECONDARY}
            width={'50%'}
          />
        </Box>
        <Box>
          <Calendar
            className="calendar"
            minDate={minDate}
            maxDate={maxDate}
            selectRange={true}
            allowPartialRange={true}
            value={[fromDate, toDate]}
            onChange={handleChange}
          />
        </Box>
        <Box css={{ display: 'flex', justifyContent: 'center' }}>
          <Button id="select-date-range-submit" type="submit">
            {t('select_date_range_modal.apply_date_range')}
          </Button>
        </Box>
      </Form>
    </FormProvider>
  );
};

export default DateRangeForm;
